import * as XLSX from "xlsx";
import {
  number,
  priceAfterTax,
  totalPrice,
} from "../commonFuntions/CommonUtilFunctions";
import { unitOptions } from "../modules/ManageOrders/OrderDetails/OrderConstant/Constant";

const EXCEL_FILE_PATTERN = /\.(xls|xlsx)$/i;
const YIELD_EVERY_ROWS = 25;

export const quoteExcelAccept =
  ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const yieldToMainThread = () =>
  new Promise((resolve) => {
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });

const getFiniteNumber = (value, fallback = 0) => {
  const parsedValue = number(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getBeforeTaxValue = (item = {}) =>
  item?.price_before_tax ??
  item?.price_fore_tax ??
  item?.price_pre_tax ??
  item?.before_tax ??
  0;

const PERCENTAGE_MARGIN_TYPES = ["%", "percent", "percentage"];

const getMarginType = (item = {}) => {
  const marginType =
    item?.mob_margin?.margin_type ??
    item?.mob_margin?.amount_in ??
    item?.mob_margin_type ??
    item?.margin_type ??
    item?.amount_in;

  return PERCENTAGE_MARGIN_TYPES.includes(
    String(marginType || "").trim().toLowerCase()
  )
    ? "percentage"
    : "amount";
};

const getMarginValue = (item = {}) =>
  item?.mob_margin?.value ??
  item?.mob_margin?.amount ??
  item?.mob_margin_value ??
  item?.amount ??
  0;

const hasMarginValue = (item = {}) => {
  const marginValue =
    item?.mob_margin?.value ??
    item?.mob_margin?.amount ??
    item?.mob_margin_value ??
    item?.amount;

  return marginValue !== undefined && marginValue !== null && marginValue !== "";
};

// Mirrors GstCell's handleSaveMobMargin so imported rows match manually entered ones
const getBmpPrice = ({ afterTax, quantity, amountIn, amount }) => {
  const grossTotal = afterTax * quantity;

  if (amountIn === "amount") {
    return Math.max(grossTotal - amount * quantity, 0);
  }

  return grossTotal - (grossTotal * amount) / 100;
};

const isPlaceholderRow = (row = {}) =>
  !row?.input &&
  !row?.unit_name &&
  !row?.imageURL &&
  !row?.hsn_sac &&
  !getFiniteNumber(row?.before_tax) &&
  !getFiniteNumber(row?.gst) &&
  !getFiniteNumber(row?.after_tax) &&
  !getFiniteNumber(row?.discount) &&
  !getFiniteNumber(row?.quantity) &&
  !getFiniteNumber(row?.total) &&
  !row?.mob_sku;

const getSanitizedTableMeta = (tableRows = [], fallbackKeyCount = 0) => {
  let hasPlaceholder = false;
  let maxKey = Number(fallbackKeyCount) || 0;
  const sanitizedRows = [];

  for (const row of tableRows) {
    if (isPlaceholderRow(row)) {
      hasPlaceholder = true;
      continue;
    }

    sanitizedRows.push(row);
    maxKey = Math.max(maxKey, Number(row?.key) || 0);
  }

  return {
    rows: hasPlaceholder ? sanitizedRows : tableRows,
    maxKey,
  };
};

// "  Sq. Ft " → "SQ FT", "No." → "NO"
const normalizeUnit = (value) =>
  String(value ?? "")
    .toUpperCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();

const UNIT_CODES = new Set(unitOptions.map((unit) => unit.code));

// Built from unitOptions so new units are picked up automatically. Each plural
// description is also matched in singular ("BOTTLES" → also "BOTTLE").
const UNIT_CODE_BY_DESCRIPTION = new Map();
unitOptions.forEach(({ code, description }) => {
  const name = normalizeUnit(description);
  if (!UNIT_CODE_BY_DESCRIPTION.has(name)) UNIT_CODE_BY_DESCRIPTION.set(name, code);
  const singular = name.endsWith("S") ? name.slice(0, -1) : null;
  if (singular && !UNIT_CODE_BY_DESCRIPTION.has(singular)) {
    UNIT_CODE_BY_DESCRIPTION.set(singular, code);
  }
});

// Only what codes and descriptions can't cover: catalogue words ("Count"),
// common short forms and alternate spellings
const UNIT_ALIASES = {
  COUNT: "NOS",
  NO: "NOS",
  NUM: "NOS",
  PC: "PCS",
  KG: "KGS",
  KILO: "KGS",
  G: "GMS",
  GM: "GMS",
  GRAM: "GMS",
  GRAMS: "GMS",
  L: "LTR",
  LITER: "LTR",
  LITERS: "LTR",
  ML: "MLT",
  MILLILITRE: "MLT",
  MILLILITER: "MLT",
  M: "MTR",
  METRE: "MTR",
  METRES: "MTR",
  CM: "CMS",
  CENTIMETRE: "CMS",
  CENTIMETRES: "CMS",
  KM: "KME",
  KILOMETER: "KME",
  KILOMETERS: "KME",
  KILOMETRES: "KME",
  SQFT: "SQF",
  "SQ FT": "SQF",
  SQMT: "SQM",
  "SQ MT": "SQM",
  "SQ M": "SQM",
  PKT: "PAC",
  PACKET: "PAC",
  PACKETS: "PAC",
  TONNE: "TON",
};

// Maps an Excel/API unit ("UNT", "unt", "Units", "Unit", "Bottle", "Kg") to a
// unitOptions code; anything unrecognised returns "" so the user picks a unit
export const toUnitCode = (value) => {
  const unit = normalizeUnit(value);
  if (!unit) return "";
  if (UNIT_CODES.has(unit)) return unit;
  return UNIT_CODE_BY_DESCRIPTION.get(unit) || UNIT_ALIASES[unit] || "";
};

const buildImportedRow = (item = {}, key) => {
  const beforeTax = getFiniteNumber(getBeforeTaxValue(item));
  const gst = getFiniteNumber(item?.gst);
  const discount = getFiniteNumber(item?.discount);
  const quantity = getFiniteNumber(item?.quantity);
  const amountIn = getMarginType(item);
  const amount = getFiniteNumber(getMarginValue(item));
  const afterTax =
    item?.price_after_tax !== undefined && item?.price_after_tax !== null
      ? getFiniteNumber(item?.price_after_tax)
      : priceAfterTax({
          before_tax: beforeTax,
          gst,
        });
  const total =
    item?.total !== undefined && item?.total !== null
      ? getFiniteNumber(item?.total)
      : totalPrice({
          before_tax: beforeTax,
          after_tax: afterTax,
          gst,
          discount,
          quantity,
        });

  return {
    key,
    input: item?.product_name || item?.item_name || "",
    // Prefer the unit typed in the Excel; the API may have swapped in the catalogue unit
    unit_name:
      toUnitCode(item?.excel_unit_name) || toUnitCode(item?.unit_name || item?.unit),
    imageURL: item?.product_image || "",
    hsn_sac: item?.hsn_sac || item?.hsn_code || "",
    before_tax: beforeTax,
    gst,
    after_tax: afterTax,
    amount_in: amountIn,
    amount,
    bmp_price:
      item?.bmp_price !== undefined && item?.bmp_price !== null
        ? getFiniteNumber(item?.bmp_price)
        : hasMarginValue(item)
        ? getBmpPrice({ afterTax, quantity, amountIn, amount })
        : "",
    discount,
    quantity,
    total,
    mob_margin: { amount_in: amountIn, amount },
  };
};

const MARGIN_TYPE_HEADERS = ["mobmargintype", "margintype"];
// Same header aliases the backend accepts for the unit column
const UNIT_HEADERS = ["unit", "unitname"];

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const cleanCellValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "string") {
    return value.trim() || null;
  }

  return value;
};

// The preview API drops some Excel values: margin types other than
// "percentage"/"amount" (e.g. "%", "rs"), and the unit whenever the SKU's catalogue
// product has its own unit. So read those raw columns from the file itself.
// Row skipping mirrors the backend parser so the arrays line up with the preview rows.
// Returns Map(sheetNumber → [{ marginType, unit }]).
export const readQuoteExcelRawColumns = async (file) => {
  const rawColumnsBySheet = new Map();

  try {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });

    workbook.SheetNames.forEach((sheetName, sheetIndex) => {
      const sheetRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
        defval: null,
        blankrows: true,
        raw: true,
      });
      let headerColumns = null;
      let marginTypeColumn = -1;
      let unitColumn = -1;
      const rawRows = [];

      for (const sheetRow of sheetRows) {
        const cleanedRow = (sheetRow || []).map(cleanCellValue);

        if (!headerColumns) {
          if (cleanedRow.every((cell) => cell === null)) {
            continue;
          }

          headerColumns = cleanedRow
            .map((cell, columnIndex) => (cell === null ? -1 : columnIndex))
            .filter((columnIndex) => columnIndex >= 0);
          const findColumn = (headers) =>
            cleanedRow.findIndex((cell) => headers.includes(normalizeHeader(cell)));
          marginTypeColumn = findColumn(MARGIN_TYPE_HEADERS);
          unitColumn = findColumn(UNIT_HEADERS);
          continue;
        }

        if (!headerColumns.some((columnIndex) => cleanedRow[columnIndex] !== null)) {
          continue;
        }

        rawRows.push({
          marginType: marginTypeColumn >= 0 ? cleanedRow[marginTypeColumn] ?? null : null,
          unit: unitColumn >= 0 ? cleanedRow[unitColumn] ?? null : null,
        });
      }

      rawColumnsBySheet.set(sheetIndex + 1, rawRows);
    });
  } catch (error) {
    return new Map();
  }

  return rawColumnsBySheet;
};

export const applyQuoteExcelRawColumns = (
  previewGroups = [],
  rawColumnsBySheet = new Map()
) =>
  previewGroups.map((group) => {
    const rawRows = rawColumnsBySheet.get(group.index);

    // Skip the sheet rather than risk assigning values to the wrong rows
    if (!rawRows || rawRows.length !== group.rows.length) {
      return group;
    }

    return {
      ...group,
      rows: group.rows.map((row, rowIndex) => {
        const { marginType, unit } = rawRows[rowIndex];
        let nextRow = row;
        if (marginType !== null) {
          nextRow = {
            ...nextRow,
            mob_margin: { ...(nextRow?.mob_margin || {}), margin_type: String(marginType) },
          };
        }
        if (unit !== null) {
          nextRow = { ...nextRow, excel_unit_name: String(unit) };
        }
        return nextRow;
      }),
    };
  });

export const isExcelFile = (file) => {
  if (!file?.name) {
    return false;
  }

  return EXCEL_FILE_PATTERN.test(file.name);
};

export const extractQuoteExcelPreviewGroups = (response) => {
  const previewData =
    response?.data?.data ??
    response?.data?.preview_data ??
    response?.data?.preview ??
    response?.data ??
    {};

  if (!previewData || typeof previewData !== "object") {
    return [];
  }

  return Object.entries(previewData)
    .map(([index, rows]) => ({
      index: Number(index),
      rows: Array.isArray(rows) ? rows : [],
    }))
    .filter(
      ({ index, rows }) => Number.isInteger(index) && index >= 0 && rows.length
    )
    .sort((firstGroup, secondGroup) => firstGroup.index - secondGroup.index);
};

export const mergeQuoteExcelPreviewData = async ({
  count = [],
  dataSource = [],
  previewGroups = [],
  selectedSeller = [],
  startIndex = 0,
}) => {
  const nextDataSource = [...dataSource];
  const nextSelectedSeller = [...selectedSeller];
  const nextCount = [...count];
  const sanitizedTables = new Map();
  let processedRows = 0;
  const basePreviewIndex = previewGroups?.length ? previewGroups[0].index : 0;

  for (let groupIndex = 0; groupIndex < previewGroups.length; groupIndex += 1) {
    const group = previewGroups[groupIndex];
    const relativeGroupIndex = Math.max(group.index - basePreviewIndex, 0);
    const targetTableIndex = startIndex + relativeGroupIndex;

    while (nextDataSource.length <= targetTableIndex) {
      nextDataSource.push([]);
    }

    while (nextSelectedSeller.length <= targetTableIndex) {
      nextSelectedSeller.push({});
    }

    while (nextCount.length <= targetTableIndex) {
      nextCount.push(0);
    }

    if (!sanitizedTables.has(targetTableIndex)) {
      sanitizedTables.set(
        targetTableIndex,
        getSanitizedTableMeta(
          nextDataSource[targetTableIndex] || [],
          nextCount[targetTableIndex]
        )
      );
    }

    const tableMeta = sanitizedTables.get(targetTableIndex);
    const existingRows = tableMeta?.rows || [];
    let nextKey = tableMeta?.maxKey || 0;
    const importedRows = [];

    for (const row of group.rows) {
      nextKey += 1;
      importedRows.push(buildImportedRow(row, nextKey));
      processedRows += 1;

      if (processedRows % YIELD_EVERY_ROWS === 0) {
        await yieldToMainThread();
      }
    }

    nextDataSource[targetTableIndex] = [...existingRows, ...importedRows];
    sanitizedTables.set(targetTableIndex, {
      rows: nextDataSource[targetTableIndex],
      maxKey: nextKey,
    });
    nextSelectedSeller[targetTableIndex] =
      nextSelectedSeller[targetTableIndex] || {};
    nextCount[targetTableIndex] = nextKey;
  }

  return {
    count: nextCount,
    dataSource: nextDataSource,
    selectedSeller: nextSelectedSeller,
  };
};
